
import { useState, useCallback, useRef } from 'react';
import {
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  ReactFlowInstance
} from '@xyflow/react';
import { initialNodes, initialEdges } from '../initialPolicyFlow';
import { useToast } from '@/hooks/use-toast';

export const useFlowEditor = (policyId?: number) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const { toast } = useToast();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  // Handle connection between nodes
  const onConnect = useCallback((params: Connection) => {
    setEdges((eds) => addEdge({
      ...params,
      animated: true,
      type: 'smoothstep',
    }, eds));
  }, [setEdges]);

  // Handle node selection
  const onNodeClick = useCallback((event: React.MouseEvent, node: any) => {
    setSelectedNode(node);
  }, []);

  // Handle drop from node panel
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');
      
      if (typeof type === 'undefined' || !type || !reactFlowBounds || !reactFlowInstance) {
        return;
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNode = {
        id: `${type}-${Date.now()}`,
        type,
        position,
        data: { label: `New ${type} node` },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes]
  );

  // Save policy flow
  const handleSave = () => {
    console.log('Saving policy flow:', { nodes, edges });
    toast({
      title: 'Policy flow saved',
      description: 'Your governance policy flow has been saved successfully.',
    });
  };

  // Run/test policy flow
  const handleRun = () => {
    toast({
      title: 'Running policy',
      description: 'Policy flow execution started. Check results in the log.',
    });
  };

  return {
    nodes,
    edges,
    selectedNode,
    reactFlowInstance,
    reactFlowWrapper,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onNodeClick,
    onDragOver,
    onDrop,
    setReactFlowInstance,
    setSelectedNode,
    setNodes,
    handleSave,
    handleRun
  };
};
